import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ReportTable = ({ reportData = [], onExport }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const mockReportData = [
    {
      id: 'RPT-001',
      contentId: 'CNT-2024-001',
      platform: 'Facebook',
      team: 'Marketing',
      violationType: 'Missing Disclosure',
      severity: 'High',
      reviewer: 'Sarah Chen',
      status: 'Resolved',
      timestamp: '2024-09-20T10:30:00Z',
      resolutionTime: '2.5 hours'
    },
    {
      id: 'RPT-002',
      contentId: 'CNT-2024-002',
      platform: 'LinkedIn',
      team: 'Social Media',
      violationType: 'Misleading Claims',
      severity: 'Critical',
      reviewer: 'Michael Rodriguez',
      status: 'Under Review',
      timestamp: '2024-09-20T09:15:00Z',
      resolutionTime: 'Pending'
    },
    {
      id: 'RPT-003',
      contentId: 'CNT-2024-003',
      platform: 'Twitter',
      team: 'Content',
      violationType: 'Risk Warning',
      severity: 'Medium',
      reviewer: 'Emily Johnson',
      status: 'Resolved',
      timestamp: '2024-09-19T16:45:00Z',
      resolutionTime: '1.2 hours'
    },
    {
      id: 'RPT-004',
      contentId: 'CNT-2024-004',
      platform: 'Website',
      team: 'Marketing',
      violationType: 'Regulatory Violation',
      severity: 'High',
      reviewer: 'David Kim',
      status: 'Escalated',
      timestamp: '2024-09-19T14:20:00Z',
      resolutionTime: '4.8 hours'
    },
    {
      id: 'RPT-005',
      contentId: 'CNT-2024-005',
      platform: 'Instagram',
      team: 'Social Media',
      violationType: 'Advertising Standards',
      severity: 'Low',
      reviewer: 'Lisa Wang',
      status: 'Resolved',
      timestamp: '2024-09-18T11:30:00Z',
      resolutionTime: '0.8 hours'
    }
  ];

  const data = reportData?.length > 0 ? reportData : mockReportData;

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'text-error bg-error/10';
      case 'High': return 'text-warning bg-warning/10';
      case 'Medium': return 'text-accent bg-accent/10';
      case 'Low': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved': return 'text-success bg-success/10';
      case 'Under Review': return 'text-warning bg-warning/10';
      case 'Escalated': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedData = [...data]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const paginatedData = sortedData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(sortedData?.length / itemsPerPage);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Compliance Report Data</h3>
          <p className="text-sm text-muted-foreground">{sortedData?.length} violations found</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={() => onExport('csv')}
          >
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            iconPosition="left"
            onClick={() => onExport('pdf')}
          >
            PDF
          </Button>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              {[
                { key: 'contentId', label: 'Content ID' },
                { key: 'platform', label: 'Platform' },
                { key: 'team', label: 'Team' },
                { key: 'violationType', label: 'Violation Type' },
                { key: 'severity', label: 'Severity' },
                { key: 'reviewer', label: 'Reviewer' },
                { key: 'status', label: 'Status' },
                { key: 'timestamp', label: 'Date' },
                { key: 'resolutionTime', label: 'Resolution Time' }
              ]?.map((column) => (
                <th
                  key={column?.key}
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/70 transition-micro"
                  onClick={() => handleSort(column?.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column?.label}</span>
                    {sortConfig?.key === column?.key && (
                      <Icon
                        name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                        size={14}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedData?.map((item) => (
              <tr key={item?.id} className="hover:bg-muted/30 transition-micro">
                <td className="px-4 py-3 text-sm font-medium text-foreground">{item?.contentId}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{item?.platform}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{item?.team}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{item?.violationType}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(item?.severity)}`}>
                    {item?.severity}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{item?.reviewer}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item?.status)}`}>
                    {item?.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{formatTimestamp(item?.timestamp)}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{item?.resolutionTime}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {paginatedData?.map((item) => (
          <div key={item?.id} className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">{item?.contentId}</span>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSeverityColor(item?.severity)}`}>
                {item?.severity}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Platform:</span>
                <span className="ml-1 text-foreground">{item?.platform}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Team:</span>
                <span className="ml-1 text-foreground">{item?.team}</span>
              </div>
              <div className="col-span-2">
                <span className="text-muted-foreground">Violation:</span>
                <span className="ml-1 text-foreground">{item?.violationType}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(item?.status)}`}>
                {item?.status}
              </span>
              <span className="text-xs text-muted-foreground">{formatTimestamp(item?.timestamp)}</span>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedData?.length)} of {sortedData?.length} results
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            />
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportTable;